<template>
        <div class="min-h-screen bg-gray-50">
                <div class="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center flex-wrap gap-4">
                        <div class="flex flex-col gap-2">
                                <router-link to="/layout" class="flex items-center gap-2 text-gray-500 no-underline text-sm hover:text-orange-7">
                                        <i class="fa fa-arrow-left"></i>
                                        <span>Back to Layouts</span>
                                </router-link>
                                <h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Layout</h1>
                        </div>
                        <button
                                v-if="!isLoading"
                                class="bg-orange-7 text-white border-none rounded-md px-5 py-2.5 text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                :disabled="isSaving"
                                @click="saveLayout"
                        >
                                {{ isSaving ? 'Saving...' : 'Save Changes' }}
                        </button>
                </div>

                <div class="p-6">
                        <div v-if="isLoading" class="py-12 px-6 text-center text-gray-500">
                                Loading...
                        </div>

                        <div v-else>
                                <!-- Tabs -->
                                <div class="flex gap-2 mb-4">
                                        <button
                                                class="px-4 py-2 rounded-md text-sm font-medium cursor-pointer border-none transition-colors"
                                                :class="activeTab === 'properties' ? 'bg-orange-7 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'"
                                                @click="activeTab = 'properties'"
                                        >
                                                Layout Properties
                                        </button>
                                        <button
                                                class="px-4 py-2 rounded-md text-sm font-medium cursor-pointer border-none transition-colors"
                                                :class="activeTab === 'templates' ? 'bg-orange-7 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'"
                                                @click="activeTab = 'templates'"
                                        >
                                                Layout Templates
                                        </button>
                                </div>

                                <!-- Properties Tab -->
                                <div v-show="activeTab === 'properties'">
                                        <div class="bg-white rounded-lg shadow-sm overflow-hidden">
                                                <div class="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                                                        <h2 class="text-lg font-semibold text-gray-900 m-0">Properties</h2>
                                                </div>
                                                <div class="p-6">
                                                        <div class="mb-4">
                                                                <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                                                <input
                                                                        v-model="layout.name"
                                                                        type="text"
                                                                        class="w-full max-w-[400px] px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:border-orange-7 outline-none"
                                                                >
                                                        </div>

                                                        <div class="mb-4">
                                                                <label class="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                                                                <div class="flex items-center gap-3">
                                                                        <button
                                                                                class="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                                                                                @click="uploadIcon"
                                                                        >
                                                                                <i class="fa fa-upload"></i>
                                                                                Choose Icon
                                                                        </button>
                                                                        <span class="text-sm text-gray-500">{{ iconFileName || 'No file selected' }}</span>
                                                                </div>
                                                                <div v-if="layout.icon" class="mt-3 flex items-center gap-3">
                                                                        <img
                                                                                :src="layout.icon"
                                                                                class="w-12 h-12 object-contain rounded border border-gray-200"
                                                                                alt="Icon preview"
                                                                        >
                                                                        <button
                                                                                class="inline-flex items-center gap-1 px-3 py-1.5 text-red-600 bg-red-50 border border-red-200 rounded text-sm cursor-pointer hover:bg-red-100"
                                                                                @click="removeIcon"
                                                                        >
                                                                                <i class="fa fa-times"></i> Remove
                                                                        </button>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>

                                <!-- Templates Tab -->
                                <div v-show="activeTab === 'templates'">
                                        <div class="bg-white rounded-lg shadow-sm overflow-hidden">
                                                <div class="flex justify-between items-center px-6 py-4 border-b border-gray-200 flex-wrap gap-3">
                                                        <h2 class="text-lg font-semibold text-gray-900 m-0">Linked Templates</h2>
                                                        <div class="flex gap-2">
                                                                <button
                                                                        class="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                                                                        @click="showNewTemplateForm = !showNewTemplateForm"
                                                                >
                                                                        {{ showNewTemplateForm ? 'Cancel' : '+ Create New Template' }}
                                                                </button>
                                                                <button
                                                                        class="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                                                                        @click="showExistingTemplateForm = !showExistingTemplateForm"
                                                                >
                                                                        {{ showExistingTemplateForm ? 'Cancel' : '+ Add Existing Template' }}
                                                                </button>
                                                        </div>
                                                </div>

                                                <!-- Create New Template Form -->
                                                <div v-if="showNewTemplateForm" class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                                        <div class="flex items-end gap-4 flex-wrap">
                                                                <div class="flex-1 min-w-[200px]">
                                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                                                                        <input
                                                                                v-model="newTemplateName"
                                                                                type="text"
                                                                                placeholder="Enter template name"
                                                                                class="w-full max-w-[400px] px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:border-orange-7 outline-none"
                                                                        >
                                                                </div>
                                                                <button
                                                                        class="px-4 py-2 bg-orange-7 text-white border-none rounded-md text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                        :disabled="!newTemplateName.trim() || isCreatingTemplate"
                                                                        @click="createNewTemplate"
                                                                >
                                                                        {{ isCreatingTemplate ? 'Creating...' : 'Add Template' }}
                                                                </button>
                                                        </div>
                                                </div>

                                                <!-- Add Existing Template Form -->
                                                <div v-if="showExistingTemplateForm" class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                                        <div class="flex items-end gap-4 flex-wrap">
                                                                <div class="flex-1 min-w-[200px]">
                                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Select Template</label>
                                                                        <select
                                                                                v-model="selectedExistingTemplateId"
                                                                                class="w-full max-w-[400px] px-3 py-2 border border-gray-300 rounded-md text-sm bg-white box-border focus:border-orange-7 outline-none"
                                                                        >
                                                                                <option value="">Select a template...</option>
                                                                                <option
                                                                                        v-for="tpl in availableTemplates"
                                                                                        :key="tpl.id"
                                                                                        :value="tpl.id"
                                                                                >
                                                                                        {{ tpl.name }} (ID: {{ tpl.id }})
                                                                                </option>
                                                                        </select>
                                                                </div>
                                                                <button
                                                                        class="px-4 py-2 bg-orange-7 text-white border-none rounded-md text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                        :disabled="!selectedExistingTemplateId || isAddingExisting"
                                                                        @click="addExistingTemplate"
                                                                >
                                                                        {{ isAddingExisting ? 'Adding...' : 'Add Template' }}
                                                                </button>
                                                        </div>
                                                </div>

                                                <div v-if="isLoadingTemplates" class="py-12 px-6 text-center text-gray-500">
                                                        Loading templates...
                                                </div>

                                                <div v-else class="overflow-x-auto">
                                                        <table class="w-full border-collapse">
                                                                <thead>
                                                                        <tr>
                                                                                <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase bg-gray-50 border-b border-gray-200">Name</th>
                                                                                <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase bg-gray-50 border-b border-gray-200">Serial Number</th>
                                                                                <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase bg-gray-50 border-b border-gray-200">Page Repeat</th>
                                                                                <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase bg-gray-50 border-b border-gray-200 w-[120px]">Actions</th>
                                                                        </tr>
                                                                </thead>
                                                                <tbody>
                                                                        <tr
                                                                                v-for="link in linkedTemplates"
                                                                                :key="link.id"
                                                                                class="border-b border-gray-200 hover:bg-gray-50"
                                                                        >
                                                                                <td class="px-6 py-4 text-sm text-gray-900">{{ link.name || `Template ${link.templateid}` }}</td>
                                                                                <td class="px-6 py-4 text-sm text-gray-500">{{ link.serialnumber }}</td>
                                                                                <td class="px-6 py-4">
                                                                                        <label class="relative inline-flex items-center cursor-pointer">
                                                                                                <input
                                                                                                        type="checkbox"
                                                                                                        :checked="Boolean(link.repeat)"
                                                                                                        class="sr-only peer"
                                                                                                        @change="toggleRepeat(link, $event)"
                                                                                                >
                                                                                                <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-7"></div>
                                                                                        </label>
                                                                                </td>
                                                                                <td class="px-6 py-4">
                                                                                        <div class="flex gap-2">
                                                                                                <button
                                                                                                        class="p-2 text-gray-500 bg-transparent border-none rounded cursor-pointer hover:text-orange-7 hover:bg-orange-1"
                                                                                                        title="Edit Template"
                                                                                                        @click="editTemplate(link.templateid)"
                                                                                                >
                                                                                                        <i class="fa fa-edit"></i>
                                                                                                </button>
                                                                                                <button
                                                                                                        class="p-2 text-gray-500 bg-transparent border-none rounded cursor-pointer hover:text-red-600 hover:bg-red-50"
                                                                                                        title="Remove from Layout"
                                                                                                        @click="confirmRemoveTemplate(link)"
                                                                                                >
                                                                                                        <i class="fa fa-trash"></i>
                                                                                                </button>
                                                                                        </div>
                                                                                </td>
                                                                        </tr>
                                                                        <tr v-if="linkedTemplates.length === 0">
                                                                                <td class="px-6 py-4" colspan="4">
                                                                                        <div class="text-center py-8 text-gray-500">No templates linked to this layout</div>
                                                                                </td>
                                                                        </tr>
                                                                </tbody>
                                                        </table>
                                                </div>
                                        </div>
                                </div>

                                <!-- Mobile Save Button -->
                                <div class="hidden py-4">
                                        <button
                                                class="w-full bg-orange-7 text-white border-none rounded-md px-5 py-2.5 text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                                :disabled="isSaving"
                                                @click="saveLayout"
                                        >
                                                {{ isSaving ? 'Saving...' : 'Save Changes' }}
                                        </button>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { LayoutModel, LayoutTemplateLinkModel, TemplateModel } from '@sosocio/creator/interfaces';
import { PickerResponse } from 'filestack-js';
import Swal from 'sweetalert2';
import LayoutService from 'services/LayoutService';
import LayoutTemplateLinkService from 'services/LayoutTemplateLinkService';
import TemplateService from 'services/TemplateService';

interface LinkedTemplate extends LayoutTemplateLinkModel {
        name?: string;
}

export default defineComponent({
        name: 'EditLayoutPage',
        setup() {
                const route = useRoute();
                const router = useRouter();
                const instance = getCurrentInstance();
                const filestackClient = instance?.appContext.config.globalProperties.$client;

                const layoutId: number = Number(Array.isArray(route.params.id) ? route.params.id[0] : route.params.id);

                const layout = ref<Partial<LayoutModel>>({});
                const linkedTemplates = ref<LinkedTemplate[]>([]);
                const availableTemplates = ref<TemplateModel[]>([]);
                const isLoading = ref(true);
                const isSaving = ref(false);
                const isLoadingTemplates = ref(false);
                const activeTab = ref('properties');
                const iconFileName = ref('');
                const showNewTemplateForm = ref(false);
                const showExistingTemplateForm = ref(false);
                const newTemplateName = ref('');
                const selectedExistingTemplateId = ref<number | ''>('');
                const isCreatingTemplate = ref(false);
                const isAddingExisting = ref(false);

                const loadLayout = async () => {
                        isLoading.value = true;
                        try {
                                const data = await LayoutService.get(layoutId);
                                layout.value = data;
                        } catch (error: any) {
                                console.error('Failed to load layout:', error);
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to load layout',
                                        icon: 'error',
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const loadAvailableTemplates = async () => {
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const templates = await TemplateService.getAll(params);
                                availableTemplates.value = templates;
                        } catch (error: any) {
                                console.error('Failed to load templates:', error);
                        }
                };

                const loadLinkedTemplates = async () => {
                        isLoadingTemplates.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const links = await LayoutService.getTemplateLinks(layoutId, params);
                                const enrichedLinks: LinkedTemplate[] = links.map(link => {
                                        const template = availableTemplates.value.find(t => t.id === link.templateid);
                                        return {
                                                ...link,
                                                name: template?.name || `Template ${link.templateid}`,
                                        };
                                });
                                linkedTemplates.value = enrichedLinks;
                        } catch (error: any) {
                                console.error('Failed to load linked templates:', error);
                        } finally {
                                isLoadingTemplates.value = false;
                        }
                };

                const saveLayout = async () => {
                        isSaving.value = true;
                        try {
                                await LayoutService.update(layoutId, layout.value);
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: 'Layout saved successfully',
                                        showConfirmButton: false,
                                        timer: 3000,
                                });
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to save layout',
                                        icon: 'error',
                                });
                        } finally {
                                isSaving.value = false;
                        }
                };

                const uploadIcon = () => {
                        if (!filestackClient) {
                                console.error('Filestack client not available');
                                Swal.fire({
                                        title: 'Error',
                                        text: 'File upload service is not available. Please try again later.',
                                        icon: 'error',
                                });
                                return;
                        }
                        filestackClient.picker({
                                accept: 'image/*',
                                maxFiles: 1,
                                onUploadDone: (res: PickerResponse) => {
                                        if (res.filesUploaded.length > 0) {
                                                const file = res.filesUploaded[0];
                                                layout.value.icon = file.url;
                                                iconFileName.value = file.filename;
                                        }
                                },
                        }).open();
                };

                const removeIcon = () => {
                        layout.value.icon = '';
                        iconFileName.value = '';
                };

                const createNewTemplate = async () => {
                        if (!newTemplateName.value.trim()) return;
                        isCreatingTemplate.value = true;
                        try {
                                const newTemplate = await TemplateService.create({ name: newTemplateName.value.trim() });
                                await LayoutTemplateLinkService.create({
                                        layoutid: layoutId as number,
                                        templateid: Number(newTemplate.id),
                                });
                                await loadAvailableTemplates();
                                await loadLinkedTemplates();
                                newTemplateName.value = '';
                                showNewTemplateForm.value = false;
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: 'Template created and linked',
                                        showConfirmButton: false,
                                        timer: 3000,
                                });
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to create template',
                                        icon: 'error',
                                });
                        } finally {
                                isCreatingTemplate.value = false;
                        }
                };

                const addExistingTemplate = async () => {
                        if (!selectedExistingTemplateId.value) return;
                        isAddingExisting.value = true;
                        try {
                                await LayoutTemplateLinkService.create({
                                        layoutid: layoutId,
                                        templateid: selectedExistingTemplateId.value as number,
                                });
                                await loadLinkedTemplates();
                                selectedExistingTemplateId.value = '';
                                showExistingTemplateForm.value = false;
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: 'Template linked successfully',
                                        showConfirmButton: false,
                                        timer: 3000,
                                });
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to link template',
                                        icon: 'error',
                                });
                        } finally {
                                isAddingExisting.value = false;
                        }
                };

                const toggleRepeat = async (link: LinkedTemplate, event: Event) => {
                        const target = event.target as HTMLInputElement;
                        try {
                                await LayoutTemplateLinkService.update(link.id, {
                                        repeat: target.checked ? 1 : 0,
                                });
                                link.repeat = target.checked ? 1 : 0;
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to update template',
                                        icon: 'error',
                                });
                                target.checked = !target.checked;
                        }
                };

                const editTemplate = (templateId: number) => {
                        router.push(`/template/${templateId}`);
                };

                const confirmRemoveTemplate = async (link: LinkedTemplate) => {
                        const result = await Swal.fire({
                                title: 'Remove Template?',
                                text: 'This will unlink the template from this layout.',
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                confirmButtonText: 'Remove',
                        });
                        if (result.isConfirmed) {
                                try {
                                        await LayoutTemplateLinkService.delete(link.id);
                                        await loadLinkedTemplates();
                                        Swal.fire({
                                                toast: true,
                                                position: 'top-end',
                                                icon: 'success',
                                                title: 'Template removed',
                                                showConfirmButton: false,
                                                timer: 3000,
                                        });
                                } catch (error: any) {
                                        Swal.fire({
                                                title: 'Error',
                                                text: error.message || 'Failed to remove template',
                                                icon: 'error',
                                        });
                                }
                        }
                };

                onMounted(async () => {
                        await loadLayout();
                        await loadAvailableTemplates();
                        await loadLinkedTemplates();
                });

                return {
                        layout,
                        linkedTemplates,
                        availableTemplates,
                        isLoading,
                        isSaving,
                        isLoadingTemplates,
                        activeTab,
                        iconFileName,
                        showNewTemplateForm,
                        showExistingTemplateForm,
                        newTemplateName,
                        selectedExistingTemplateId,
                        isCreatingTemplate,
                        isAddingExisting,
                        saveLayout,
                        uploadIcon,
                        removeIcon,
                        createNewTemplate,
                        addExistingTemplate,
                        toggleRepeat,
                        editTemplate,
                        confirmRemoveTemplate,
                };
        },
});
</script>
